import React, {
  createContext,
  FC,
  useCallback,
  useRef,
} from 'react';
import {
  Conversation,
  ConversationAttributes,
  ConversationMetadata,
  ConversationType,
  PrivateConversationMetadata,
  setChatInterface,
} from 'models/twilio';
import { useDispatch } from 'react-redux';

const ConversationContext = createContext({
  getConversationById: (id: string) => null as Conversation,
  addConversation: (conversation: Conversation, meta: ConversationMetadata) => {},
  removeConversationById: (id: string) => {},
  findConversationSid: (searchString: string, conversationType: ConversationType): string | null => null,
  delayExecution: (task: () => Promise<void>) => {},
  executeAllTasks: async () => {},
  processing: {
    add: (searchString: string, conversationType: ConversationType): void => {},
    remove: (searchString: string, conversationType: ConversationType): void => {},
    checkExists: (searchString: string, conversationType: ConversationType): boolean => false,
  },
  isHandlerAttached: (sid: string): boolean => false,
  recordHandlerAttachment: (sid: string) => {},
});

export const ConversationContextWrapper: FC = ({ children }) => {
  const dispatch = useDispatch();
  const handlersAttachedSidSetRef = useRef<Set<string>>(new Set());
  const conversations = useRef<Record<string, Conversation>>({});
  const taskList = useRef<Array<() => Promise<void>>>([]);
  const privateUserIds = useRef<Record<string, string>>({});
  const loaders = useRef<Record<ConversationType, string[]>>({
    [ConversationType.ANNOUNCEMENT]: [],
    [ConversationType.PRIVATE]: [],
    [ConversationType.GROUP]: [],
    [ConversationType.PUBLIC]: [],
    [ConversationType.UNKNOWN]: [],
  });

  const isHandlerAttached = useCallback((sid: string) => handlersAttachedSidSetRef.current.has(sid), []);
  const recordHandlerAttachment = useCallback((sid: string) => {
    handlersAttachedSidSetRef.current.add(sid);
  }, []);

  const getConversationById = useCallback((id: string) => conversations.current?.[id] ?? null, []);

  const addConversation = useCallback((conversation: Conversation, meta: ConversationMetadata) => {
    conversations.current[conversation.sid] = conversation;
    if ((conversation.attributes as ConversationAttributes).type === ConversationType.PRIVATE) {
      privateUserIds.current[(meta as PrivateConversationMetadata).otherUserId] = conversation.sid;
    }
  }, []);

  const removeConversationById = useCallback((id: string) => {
    delete conversations.current[id];
  }, []);

  const findConversationSid = useCallback((searchString: string, conversationType: ConversationType): string | null => {
    const allConversations = Object.values(conversations.current);
    if (conversationType === ConversationType.PRIVATE) return privateUserIds.current?.[searchString] ?? null;
    const filteredConversations = allConversations.filter((conversation) => (conversation.attributes as ConversationAttributes).type === conversationType);
    const targetConversation = filteredConversations
      .find((conversation) => conversation.uniqueName === searchString || conversation.sid === searchString) ?? null;
    return targetConversation?.sid ?? null;
  }, []);

  const pushLoading = useCallback((searchString: string, conversationType: ConversationType): void => {
    loaders.current[conversationType].push(searchString);
  }, []);

  const removeLoading = useCallback((searchString: string, conversationType: ConversationType): void => {
    loaders.current[conversationType] = loaders.current[conversationType].filter((uniq) => uniq !== searchString);
  }, []);

  const findLoading = useCallback((searchString: string, conversationType: ConversationType): boolean => {
    const target = loaders.current[conversationType].find((uniq) => uniq === searchString);
    return Boolean(target);
  }, []);

  const pushTask = useCallback((task: () => Promise<void>) => {
    taskList.current.push(task);
  }, []);

  const delayExecution = useCallback((task: () => Promise<void>) => {
    pushTask(task);
    dispatch(setChatInterface(true));
  }, [dispatch, pushTask]);

  const executeAllTasks = useCallback(async () => {
    // Execute all tasks sequentially.
    for (let i = 0; i < taskList.current.length; i += 1) {
      // eslint-disable-next-line no-await-in-loop
      await taskList.current[i]();
    }
  }, []);

  return (
    <ConversationContext.Provider value={{
      getConversationById,
      addConversation,
      removeConversationById,
      findConversationSid,
      delayExecution,
      executeAllTasks,
      processing: {
        add: pushLoading,
        remove: removeLoading,
        checkExists: findLoading,
      },
      isHandlerAttached,
      recordHandlerAttachment,
    }}
    >
      {children}
    </ConversationContext.Provider>
  );
};

export default ConversationContext;
